import React from 'react'

import { useTranslations } from 'next-intl'

import classNames from 'classnames'

import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Bookmark from '~/components/atoms/Bookmark/Bookmark'
import Button, { ButtonVariants } from '~/components/atoms/Button/Button'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import SocialShare from '~/components/atoms/SocialShare/SocialShare'
import { TextSizes } from '~/components/atoms/Text/Text'
import TextBlock from '~/components/atoms/TextBlock/TextBlock'
import ArticleFooterAuthor from '~/components/blocks/ArticleFooterAuthor/ArticleFooterAuthor'
import ArticleHeader from '~/components/blocks/ArticleHeader/ArticleHeader'
import ArticleInternalMenu from '~/components/blocks/ArticleInternalMenu/ArticleInternalMenu'
import ArticleNumberedList from '~/components/blocks/ArticleNumberedList/ArticleNumberedList'
import ArticleRelatedArticles from '~/components/blocks/ArticleRelatedArticles/ArticleRelatedArticles'
import PremiumContentBannerArticle from '~/components/blocks/PremiumContentBanner/PremiumContentBannerArticle'
import ReportCard from '~/components/blocks/ReportCard/ReportCard'
import SingleVideoModule from '~/components/blocks/SingleVideoModule/SingleVideoModule'
import { resolveBlock } from '~/components/serializers'

import DesktopStickyAdvert from './DesktopStickyAdvert'
import MobileAdvert from './MobileAdvert'

import styles from '~/styles/pages.module.css'

const blocksToWrap: string[] = []

interface StandardArticleProps {
  articleHeader: any
  video: any
  leadImage: any
  modules: any[] | null
  keySummary: any
  numberedList: any
  body: any
  advert: any
  relatedArticles: any
  relatedArtist: any
  featuredReport: any
  refs: React.MutableRefObject<React.RefObject<HTMLDivElement>[] | undefined>
  mainHeaderVisible: boolean
  hideSideMenu: boolean
  skipLinks: any
  hideToNonePremium: boolean
  isMobile: boolean
  isSmallerScreenWidth: boolean
  showDesktopStickyContent: boolean
  isValuationsOldBannerAd: boolean
  loginGatePosition: number
  valuationCTAPosition: number
  onValuationClick: () => void
  renderExpertBlock: (
    location: 'Header' | 'Footer',
    useStickyBehaviour?: boolean
  ) => React.ReactNode
}

/**
 * Renders the standard (non podcast/video) article layout: header, side
 * navigation, the article body modules, paywall handling, and the desktop
 * sticky advert / related-articles column.
 */
const StandardArticle: React.FC<StandardArticleProps> = ({
  articleHeader,
  video,
  leadImage,
  modules,
  keySummary,
  numberedList,
  body,
  advert,
  relatedArticles,
  relatedArtist,
  featuredReport,
  refs,
  mainHeaderVisible,
  hideSideMenu,
  skipLinks,
  hideToNonePremium,
  isMobile,
  isSmallerScreenWidth,
  showDesktopStickyContent,
  isValuationsOldBannerAd,
  loginGatePosition,
  valuationCTAPosition,
  onValuationClick,
  renderExpertBlock,
}) => {
  const t = useTranslations('articleContainer')

  return (
    <>
      <ArticleHeader
        {...articleHeader}
        noPaddingBottom={true}
        image={video ? video.coverImage : leadImage}
      />
      {modules && modules[0]._type == 'leadParagraph' && (
        <div
          className={styles.leadParagraphWrap}
          key={`article-module-key-${0}`}
        >
          <Container gutters={gutterTypes.always}>
            <div
              className={classNames(modules[0]._type?.toLowerCase(), {
                [styles.articleWrapper]: blocksToWrap.includes(
                  modules[0]._type
                ),
              })}
              ref={refs?.current?.[0]}
            >
              {resolveBlock(modules[0], 0, 'magazine')}
            </div>
          </Container>
        </div>
      )}
      <div className={styles.articleBlocks}>
        {renderExpertBlock('Header', true)}
        <Container gutters={gutterTypes.always}>
          <div className={styles.mainArticleContent}>
            <div
              className={classNames(
                styles.articleStickyElements,
                mainHeaderVisible && styles.articleStickyElementsPush
              )}
            >
              {hideSideMenu === false && skipLinks && skipLinks.length > 0 && (
                <ArticleInternalMenu
                  links={skipLinks}
                  category={{
                    articleType: articleHeader.articleType,
                    articleTypeSlug: articleHeader.articleTypeSlug,
                    articleTaxonomy: articleHeader.articleTaxonomy,
                    articleTypeTitle: articleHeader.articleTypeTitle,
                  }}
                />
              )}
            </div>

            <div className={styles.articlesTextColumn}>
              {video && !modules && (
                <SingleVideoModule
                  video={video}
                  title=""
                  summary=""
                  verticalPadding={false}
                  withContainer={false}
                />
              )}

              {keySummary && (
                <div className={styles.articleModulesContainer}>
                  <div className={classNames(styles.keySummary)}>
                    <TextBlock
                      body={keySummary}
                      _key={'key_summary'}
                      variant={'magazine'}
                      smallTitle
                      title={t('keyTakeaways')}
                    />
                    <span className={styles.line} />
                  </div>
                </div>
              )}

              {numberedList && (
                <ArticleNumberedList
                  {...numberedList}
                  isPremium={hideToNonePremium}
                />
              )}

              {body && (
                <div className="legacy-article-content">
                  <BlockContent
                    value={body}
                    size={TextSizes.bodyLrg}
                  ></BlockContent>
                </div>
              )}

              {modules && (
                <div className={styles.articleModulesContainer}>
                  <MobileAdvert
                    advert={advert}
                    modules={modules}
                    isValuationsOldBannerAd={isValuationsOldBannerAd}
                    isMobile={isMobile}
                  />
                  {modules.map((item, x) => {
                    const hidePaywalledAtIndex = loginGatePosition
                    const isValuationCTAPresent = x === valuationCTAPosition - 1
                    const isAfterValuationCTA = x === valuationCTAPosition
                    const showFadeOut =
                      x === hidePaywalledAtIndex && hideToNonePremium
                    const paywalled =
                      x > hidePaywalledAtIndex && hideToNonePremium
                    const classes = classNames(
                      isAfterValuationCTA && styles.articleCTAModule,
                      item._type === 'chartEmbed' && styles.articleContentBlock,
                      showFadeOut && styles.fadeOutModule,
                      paywalled && styles.hidden
                    )

                    if (item._type === 'leadParagraph') {
                      return null
                    }

                    if (item._type === 'expertQuote') {
                      item = {
                        ...item,
                        isOnArticle: true,
                      } as any
                    }

                    return (
                      <React.Fragment key={`article-module-key-${x}`}>
                        {x === 1 && video && (
                          <SingleVideoModule
                            video={video}
                            title=""
                            summary=""
                            withContainer={false}
                          />
                        )}
                        <div
                          className={classNames(
                            classes,
                            item._type?.toLowerCase(),
                            {
                              [styles.articleWrapper]: blocksToWrap.includes(
                                item._type
                              ),
                            }
                          )}
                          ref={refs?.current?.[x]}
                        >
                          {resolveBlock(item, x, 'magazine')}
                        </div>

                        {isValuationCTAPresent && isSmallerScreenWidth && (
                          <>
                            {/* Mobile Featured Report - shows inline before related articles */}
                            {featuredReport && (
                              <div className={styles.mobileFeaturedReport}>
                                <ReportCard
                                  {...featuredReport}
                                  variant="inlineFeature"
                                  matchGridHeight={false}
                                />
                              </div>
                            )}
                            <div className={styles.relatedArticlesContainer}>
                              <ArticleRelatedArticles
                                articles={relatedArticles}
                              />
                            </div>
                          </>
                        )}
                      </React.Fragment>
                    )
                  })}
                  {!hideToNonePremium ? (
                    <div className={classNames(styles.articleFooter)}>
                      <div className={styles.articleCtas}>
                        <Button
                          variant={ButtonVariants.textLink}
                          iconLeft
                          {...(!relatedArtist
                            ? {
                                launchAuthForm: 'signup',
                              }
                            : {
                                link: relatedArtist?.slug
                                  ? `/artist-${relatedArtist.slug}`
                                  : undefined,
                              })}
                        >
                          {!relatedArtist
                            ? t.rich('articleFooterSignupCtaText', {
                                em: chunks => <em>{chunks}</em>,
                              })
                            : t.rich('articleFooterArtistCtaText', {
                                artistName: relatedArtist.name,
                              })}
                        </Button>
                      </div>
                      <div className={styles.articleShare}>
                        {/* Bookmark button */}
                        <Bookmark />
                        <SocialShare
                          articleTitle={articleHeader?.title}
                          shareButtonTheme="magazine"
                        />
                      </div>
                      <ArticleFooterAuthor
                        author={articleHeader?.author}
                        reviewedBy={articleHeader?.reviewedBy}
                        publishDate={articleHeader?.publishDate}
                        updatedAt={articleHeader?.updatedAt}
                      />
                    </div>
                  ) : (
                    <PremiumContentBannerArticle />
                  )}
                </div>
              )}
            </div>

            {showDesktopStickyContent && (
              <div className={styles.articleRightColumnWrapper}>
                {relatedArticles && relatedArticles.length > 0 && (
                  <ArticleRelatedArticles articles={relatedArticles} />
                )}
                <div
                  className={classNames(
                    styles.articleStickyAdvertWrapper,
                    mainHeaderVisible && styles.articleStickyAdvertWrapperPush
                  )}
                >
                  <DesktopStickyAdvert
                    advert={advert}
                    featuredReport={featuredReport}
                    isValuationsOldBannerAd={isValuationsOldBannerAd}
                    artistName={relatedArtist?.name}
                    artistSlug={relatedArtist?.slug}
                    articleTitle={articleHeader?.title}
                    onValuationClick={onValuationClick}
                  />
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>
    </>
  )
}

export default StandardArticle
